import * as THREE from 'three';
import { GUARDIAN_POSITION } from './guardianScene';
import { SLINGSHOT_ORIGIN } from './slingshot';
import { DIE_DRILL_AXIS, getDieDrillAngle } from './dieDrill';
import { createBoardCameraFramer } from './boardCameraFraming';

export const getGuardianAttackTiming = (
  reduceMotion,
  result,
  { immediateReturn = false } = {}
) => {
  const returnsToSetup = result?.enemyHpAfter > 0 && result?.playerHpAfter > 0;
  const warden = result?.enemy?.shape === 'warden';
  if (reduceMotion)
    return {
      launchAt: 0,
      hitAt: 160,
      returnAt: 160,
      returnEndAt: 360,
      counterWindupAt: 1360,
      counterLaunchAt: 2160,
      counterAt: 2460,
      pullbackAt: result?.retaliation ? 3260 : 900,
      finishAt: result?.retaliation ? 3660 : 1300,
      returnsToSetup,
    };
  const launchAt = 600;
  const hitAt = launchAt + 950;
  const returnAt = hitAt + (immediateReturn ? 0 : 580);
  const returnEndAt = returnAt + 1100;
  // Let the return finish, then hold for a full second before telegraphing
  // the counter. Charge, flight, and impact each have their own readable beat.
  const counterWindupAt = returnEndAt + 1000;
  const counterLaunchAt = counterWindupAt + (warden ? 1250 : 900);
  const counterAt = counterLaunchAt + (warden ? 1100 : 950);
  const pullbackAt = result?.retaliation ? counterAt + 1300 : returnEndAt + 220;
  return {
    launchAt,
    hitAt,
    returnAt,
    returnEndAt,
    counterWindupAt,
    counterLaunchAt,
    counterAt,
    pullbackAt,
    // The next setup continues the close-up into its own ready-camera pullback.
    finishAt: returnsToSetup ? pullbackAt : pullbackAt + 1450,
    returnsToSetup,
  };
};

const progress = (elapsed, start, end) =>
  THREE.MathUtils.smoothstep(elapsed, start, end);

export const createGuardianAttackCamera = () => {
  const origin = new THREE.Vector3();
  const orientation = new THREE.Quaternion();
  const strikeOrientation = new THREE.Quaternion();
  const drillRotation = new THREE.Quaternion();
  const idleOrientation = new THREE.Quaternion().setFromEuler(
    new THREE.Euler(0.08, 0, 0)
  );
  const cameraStart = new THREE.Vector3();
  const lookStart = new THREE.Vector3();
  const followOffset = new THREE.Vector3();
  const closeOffset = new THREE.Vector3(0, 0.95, 2.25);
  const followPosition = new THREE.Vector3();
  const followLook = new THREE.Vector3();
  const counterPosition = new THREE.Vector3();
  const counterLook = new THREE.Vector3();
  const sling = new THREE.Vector3(
    SLINGSHOT_ORIGIN.x,
    SLINGSHOT_ORIGIN.y,
    SLINGSHOT_ORIGIN.z
  );
  let zoomStart = 1;
  let viewStart = null;
  const fitFrame = createBoardCameraFramer();
  const framePoints = Array.from({ length: 16 }, () => new THREE.Vector3());

  return {
    begin(die, camera, look) {
      origin.copy(die.position);
      orientation.copy(die.quaternion);
      cameraStart.copy(camera.position);
      lookStart.copy(look);
      zoomStart = camera.zoom;
      viewStart = camera.view?.enabled ? { ...camera.view } : null;
    },
    update({
      die,
      camera,
      look,
      elapsed,
      combat,
      reduceMotion,
      charge = 0,
      overclock = false,
      zoom = 1,
      getReadyFrame,
      target = GUARDIAN_POSITION,
      followDistance = 9.5,
      portraitWidth = 1.15,
      frameTarget = null,
      targetRadius = 0,
    }) {
      const outward = progress(elapsed, combat.launchAt, combat.hitAt);
      const returning = progress(elapsed, combat.returnAt, combat.returnEndAt);
      strikeOrientation.copy(orientation);
      if (!reduceMotion) {
        drillRotation.setFromAxisAngle(
          DIE_DRILL_AXIS,
          getDieDrillAngle(Math.min(elapsed, combat.hitAt), charge, overclock)
        );
        strikeOrientation.multiply(drillRotation);
      }
      if (elapsed < combat.returnAt) {
        die.position.lerpVectors(origin, target, outward);
        if (!reduceMotion) die.position.y += Math.sin(outward * Math.PI) * 1.8;
        die.quaternion.copy(strikeOrientation);
      } else {
        die.position.lerpVectors(target, sling, returning);
        if (!reduceMotion)
          die.position.y += Math.sin(returning * Math.PI) * 2.5;
        die.quaternion.slerpQuaternions(
          strikeOrientation,
          idleOrientation,
          returning
        );
      }
      if (reduceMotion) {
        die.position.copy(elapsed < combat.hitAt ? origin : sling);
        die.quaternion.copy(
          elapsed < combat.hitAt ? orientation : idleOrientation
        );
      }
      if (combat.frozen) {
        die.position.copy(origin);
        die.quaternion.copy(orientation);
      }

      // A low trailing view looks up toward the guardian. Portrait screens need
      // more distance so the guardian's silhouette and the flying die both fit.
      const portraitDistance = Math.max(1, portraitWidth / camera.aspect);
      followOffset
        .set(1.6, 1.65, followDistance)
        .multiplyScalar(portraitDistance);
      closeOffset
        .set(
          frameTarget ? 1.6 : 0,
          frameTarget ? 4.8 : 0.95,
          frameTarget ? 12.5 : 2.25
        )
        .multiplyScalar(Math.max(1, 0.95 / camera.aspect));
      followOffset.lerp(closeOffset, returning);
      followPosition.copy(die.position).add(followOffset);
      followLook.copy(die.position);
      followLook.y += (1 - returning) * 0.55;
      followLook.z -= (1 - returning) * 1.25;
      if (frameTarget) followLook.lerp(frameTarget, 0.35);
      const tilt = reduceMotion ? 1 : progress(elapsed, 0, combat.launchAt);
      camera.position.lerpVectors(cameraStart, followPosition, tilt);
      look.lerpVectors(lookStart, followLook, tilt);
      camera.zoom = THREE.MathUtils.lerp(zoomStart, zoom, tilt);
      if (viewStart && tilt < 1) {
        camera.setViewOffset(
          viewStart.fullWidth,
          viewStart.fullHeight,
          viewStart.offsetX * (1 - tilt),
          viewStart.offsetY * (1 - tilt),
          viewStart.width,
          viewStart.height
        );
      } else if (camera.view?.enabled) camera.clearViewOffset();

      if (combat.retaliation && !reduceMotion) {
        // Open the view back toward the enemy for its windup, then follow the
        // incoming blades toward the sling. Finish at the die for the handoff.
        const incoming = progress(
          elapsed,
          combat.counterLaunchAt,
          combat.counterAt
        );
        const counterView =
          progress(
            elapsed,
            combat.counterWindupAt - 500,
            combat.counterWindupAt + 400
          ) *
          (1 - progress(elapsed, combat.counterAt + 400, combat.pullbackAt));
        counterPosition
          .set(1.3, 3.4, 7.5)
          .multiplyScalar(Math.max(1, 0.8 / camera.aspect))
          .add(sling);
        counterLook.lerpVectors(target, sling, 0.4 + incoming * 0.4);
        camera.position.lerp(counterPosition, counterView);
        look.lerp(counterLook, counterView);
      }

      // Victories/losses finish here. A surviving encounter hands this exact
      // close-up to the next slingshot scene, which eases into its ready view.
      if (
        reduceMotion ||
        (!combat.returnsToSetup && elapsed >= combat.pullbackAt)
      ) {
        const ready = getReadyFrame();
        const pullback = reduceMotion
          ? 1
          : progress(elapsed, combat.pullbackAt, combat.finishAt - 100);
        camera.position.lerp(ready.position, pullback);
        look.lerp(ready.look, pullback);
        camera.zoom = THREE.MathUtils.lerp(camera.zoom, ready.zoom, pullback);
        camera.setViewOffset(
          ready.width,
          ready.height,
          0,
          ready.offsetY * pullback,
          ready.width,
          ready.height
        );
      }
      if (
        !reduceMotion &&
        elapsed >= combat.hitAt &&
        elapsed < combat.hitAt + 240
      ) {
        const impact = (elapsed - combat.hitAt) / 240;
        const kick = Math.sin(impact * Math.PI * 4) * (1 - impact) * 0.09;
        camera.position.x += kick;
        camera.position.y += kick * 0.55;
      }
      camera.lookAt(look);
      if (frameTarget && tilt > 0) {
        // Frame both the flying die and the Core throughout the return, rather
        // than closing in so tightly on the die that the boss leaves the shot.
        let index = 0;
        for (const [center, radius] of [
          [frameTarget, targetRadius],
          [die.position, 0.95],
        ]) {
          for (const x of [-1, 1])
            for (const y of [-1, 1])
              for (const z of [-1, 1])
                framePoints[index++]
                  .set(x * radius, y * radius, z * radius)
                  .add(center);
        }
        camera.updateMatrixWorld(true);
        const height = 1000;
        const width = height * camera.aspect;
        const fittedZoom = fitFrame(camera, framePoints, width, height, {
          top: 65,
          bottom: 65,
          side: 65,
        });
        const offsetX = camera.view.offsetX;
        const offsetY = camera.view.offsetY;
        camera.zoom = THREE.MathUtils.lerp(
          zoomStart,
          Math.min(zoom, fittedZoom),
          tilt
        );
        camera.setViewOffset(
          width,
          height,
          THREE.MathUtils.lerp(
            viewStart ? (viewStart.offsetX / viewStart.fullWidth) * width : 0,
            offsetX,
            tilt
          ),
          THREE.MathUtils.lerp(
            viewStart ? (viewStart.offsetY / viewStart.fullHeight) * height : 0,
            offsetY,
            tilt
          ),
          width,
          height
        );
      }
      camera.updateProjectionMatrix();
    },
  };
};
